import { cropToFace, type Box } from "../../server/modules/homecare/rules/home-routine";

// Photos for Home and routine (brief 1E-17): the patient's photo is cropped to the face in the
// browser before it's uploaded, with the center square when no face is found; a guide photo is made
// smaller. The face is found with pico (Nenad Markus, MIT license, github.com/nenadmarkus/picojs and
// github.com/nenadmarkus/pico): its classifier, ported below, and its face cascade, served at
// /vendor/facefinder and read only when a photo is taken. Nothing leaves the browser but the
// finished photo, and nothing is saved to the phone's camera roll.

type Classify = (r: number, c: number, s: number, pixels: Uint8Array, ldim: number) => number;

/** pico.unpack_cascade: the cascade file into its classifier. */
function unpackCascade(bytes: Uint8Array): Classify {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let p = 8;
  const tdepth = view.getInt32(p, true);
  p += 4;
  const ntrees = view.getInt32(p, true);
  p += 4;
  const leaves = 2 ** tdepth;
  const codes: number[] = [];
  const preds: number[] = [];
  const thresh: number[] = [];
  for (let t = 0; t < ntrees; t++) {
    codes.push(0, 0, 0, 0);
    for (let i = 0; i < 4 * leaves - 4; i++) codes.push(view.getInt8(p + i));
    p += 4 * leaves - 4;
    for (let i = 0; i < leaves; i++) {
      preds.push(view.getFloat32(p, true));
      p += 4;
    }
    thresh.push(view.getFloat32(p, true));
    p += 4;
  }
  const tcodes = new Int8Array(codes);
  const tpreds = new Float32Array(preds);
  const tthresh = new Float32Array(thresh);
  return (r, c, s, pixels, ldim) => {
    r *= 256;
    c *= 256;
    let root = 0;
    let o = 0;
    for (let i = 0; i < ntrees; i++) {
      let idx = 1;
      for (let j = 0; j < tdepth; j++) {
        const a =
          pixels[
            ((r + (tcodes[root + 4 * idx] as number) * s) >> 8) * ldim +
              ((c + (tcodes[root + 4 * idx + 1] as number) * s) >> 8)
          ] ?? 0;
        const b =
          pixels[
            ((r + (tcodes[root + 4 * idx + 2] as number) * s) >> 8) * ldim +
              ((c + (tcodes[root + 4 * idx + 3] as number) * s) >> 8)
          ] ?? 0;
        idx = 2 * idx + (a <= b ? 1 : 0);
      }
      o += tpreds[leaves * i + idx - leaves] as number;
      if (o <= (tthresh[i] as number)) return -1;
      root += 4 * leaves;
    }
    return o - (tthresh[ntrees - 1] as number);
  };
}

type Detection = [row: number, col: number, size: number, score: number];

/** pico.run_cascade: every window that looks like a face, at each size. */
function runCascade(
  pixels: Uint8Array,
  nrows: number,
  ncols: number,
  classify: Classify,
  minsize: number,
  maxsize: number,
): Detection[] {
  const found: Detection[] = [];
  for (let scale = minsize; scale <= maxsize; scale *= 1.1) {
    const step = Math.max(0.1 * scale, 1) >> 0;
    const offset = (scale / 2 + 1) >> 0;
    for (let r = offset; r <= nrows - offset; r += step)
      for (let c = offset; c <= ncols - offset; c += step) {
        const q = classify(r, c, scale, pixels, ncols);
        if (q > 0) found.push([r, c, scale, q]);
      }
  }
  return found;
}

/** pico.cluster_detections: overlapping windows become one face, scored by their sum. */
function cluster(dets: Detection[], iou = 0.2): Detection[] {
  const sorted = [...dets].sort((a, b) => b[3] - a[3]);
  const overlap = (a: Detection, b: Detection) => {
    const r = Math.max(
      0,
      Math.min(a[0] + a[2] / 2, b[0] + b[2] / 2) - Math.max(a[0] - a[2] / 2, b[0] - b[2] / 2),
    );
    const c = Math.max(
      0,
      Math.min(a[1] + a[2] / 2, b[1] + b[2] / 2) - Math.max(a[1] - a[2] / 2, b[1] - b[2] / 2),
    );
    return (r * c) / (a[2] * a[2] + b[2] * b[2] - r * c);
  };
  const used = new Array<boolean>(sorted.length).fill(false);
  const out: Detection[] = [];
  for (let i = 0; i < sorted.length; i++) {
    if (used[i]) continue;
    let r = 0;
    let c = 0;
    let s = 0;
    let q = 0;
    let n = 0;
    for (let j = i; j < sorted.length; j++) {
      const d = sorted[j] as Detection;
      if (overlap(sorted[i] as Detection, d) > iou) {
        used[j] = true;
        r += d[0];
        c += d[1];
        s += d[2];
        q += d[3];
        n++;
      }
    }
    out.push([r / n, c / n, s / n, q]);
  }
  return out;
}

let cascade: Promise<Classify | null> | null = null;
const loadCascade = () =>
  (cascade ??= fetch("/vendor/facefinder")
    .then((r) => (r.ok ? r.arrayBuffer() : null))
    .then((b) => (b ? unpackCascade(new Uint8Array(b)) : null))
    .catch(() => null));

/** The picture, turned the way the camera held it. */
async function bitmapOf(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  try {
    return await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    const url = URL.createObjectURL(file);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      return img;
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

/** The largest, surest face in the picture, in its own pixels; null when none is found. */
async function findFace(
  img: ImageBitmap | HTMLImageElement,
  w: number,
  h: number,
): Promise<Box | null> {
  const classify = await loadCascade();
  if (!classify) return null;
  const k = Math.min(1, 480 / Math.max(w, h));
  const sw = Math.max(1, Math.round(w * k));
  const sh = Math.max(1, Math.round(h * k));
  const canvas = document.createElement("canvas");
  canvas.width = sw;
  canvas.height = sh;
  const g = canvas.getContext("2d", { willReadFrequently: true });
  if (!g) return null;
  g.drawImage(img, 0, 0, sw, sh);
  const rgba = g.getImageData(0, 0, sw, sh).data;
  const gray = new Uint8Array(sw * sh);
  for (let i = 0; i < sw * sh; i++)
    gray[i] = (2 * (rgba[i * 4] ?? 0) + 7 * (rgba[i * 4 + 1] ?? 0) + (rgba[i * 4 + 2] ?? 0)) / 10;
  const side = Math.min(sw, sh);
  const faces = cluster(runCascade(gray, sh, sw, classify, Math.max(24, side * 0.15), side)).filter(
    (d) => d[3] > 10,
  );
  const best = faces.sort((a, b) => b[3] - a[3])[0];
  if (!best) return null;
  const [r, c, s] = best;
  return { x: (c - s / 2) / k, y: (r - s / 2) / k, width: s / k, height: s / k };
}

const jpeg = (canvas: HTMLCanvasElement, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));

export type Prepared = { ok: true; file: File; faceFound: boolean } | { ok: false; error: string };

/**
 * Makes the photo that's uploaded: for a patient, a 512-pixel square around the face (the center
 * when none is found); for the guide, the whole picture at most 1280 pixels across.
 */
export async function preparePhoto(file: File, kind: "patient" | "guide"): Promise<Prepared> {
  let img: ImageBitmap | HTMLImageElement;
  try {
    img = await bitmapOf(file);
  } catch {
    return { ok: false, error: "That photo couldn't be read. Use a JPG or PNG photo." };
  }
  const w = img.width;
  const h = img.height;
  if (!w || !h) return { ok: false, error: "That photo couldn't be read. Use a JPG or PNG photo." };
  const canvas = document.createElement("canvas");
  const g = canvas.getContext("2d");
  if (!g) return { ok: false, error: "This browser can't make the photo." };
  let faceFound = false;
  if (kind === "patient") {
    const face = await findFace(img, w, h).catch(() => null);
    faceFound = !!face;
    const box = cropToFace(face, w, h);
    canvas.width = 512;
    canvas.height = 512;
    g.drawImage(img, box.x, box.y, box.width, box.height, 0, 0, 512, 512);
  } else {
    const k = Math.min(1, 1280 / Math.max(w, h));
    canvas.width = Math.round(w * k);
    canvas.height = Math.round(h * k);
    g.drawImage(img, 0, 0, canvas.width, canvas.height);
  }
  if ("close" in img) img.close();
  const blob = await jpeg(canvas, 0.86);
  if (!blob) return { ok: false, error: "This browser can't make the photo." };
  const name = kind === "patient" ? "Patient photo.jpg" : "Home and routine photo.jpg";
  return { ok: true, file: new File([blob], name, { type: "image/jpeg" }), faceFound };
}

/** A photo shown on screen: not dragged out or held to save on a phone. */
export function photoImg(src: string, alt: string, cls: string): HTMLImageElement {
  const img = document.createElement("img");
  img.src = src;
  img.alt = alt;
  img.className = cls;
  img.draggable = false;
  img.loading = "lazy";
  img.addEventListener("contextmenu", (e) => e.preventDefault());
  return img;
}
